import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import { useForm, isFormWithHtmlDirty } from '../../helpers'

import { useUser } from '../../contexts'

import { EditorWithPreview, HtmlEditor, LeavingOnDirtyForm, PreviewPage } from '../specialized-components'
import { icons, Button, Card, ExternalLinkButton, FormField, Icon, Input, SubmitButton } from '../ui-blocks'

import lessonFields from './lesson-fields'


const parseValues = lesson => ({
  title: lesson.title,
  content: lesson.content
})


export const EditLesson = ({ lesson, onUpdate, onDelete }) => {
  const [form, setFormValues, validateForm] = useForm(lessonFields)
  const { userAvatar } = useUser()
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    setFormValues(parseValues(lesson))
  }, [lesson]) // eslint-disable-line react-hooks/exhaustive-deps


  const handleUpdate = async () => {
    const isFormValid = validateForm()

    if (!isFormValid) {
      return
    }

    setIsLoading(true)

    await onUpdate(form.values)

    setIsLoading(false)
  }


  if (!Object.keys(form.values).length) {
    return null
  }


  const isNew = !lesson.id

  const isFormDirty = isFormWithHtmlDirty(form)


  return (
    <>
      <LeavingOnDirtyForm isDirty={isFormDirty} />

      <div className="editor-header">
        <h1>{isNew ? 'New lesson' : (form.values.title || 'Edit lesson')}</h1>

        <div className="d-flex justify-content-end align-items-center">
          {!isNew && (
            <ExternalLinkButton
              href={lesson.link}
              className="mr-3"
              variant="secondary"
              disabled={isFormDirty}
            >
              Open live page
            </ExternalLinkButton>
          )}
          <SubmitButton
            onClick={handleUpdate}
            isLoading={isLoading}
            isValid={form.isValid}
          >
            {isNew ? 'Create' : 'Update'}
          </SubmitButton>
        </div>
      </div>

      <EditorWithPreview preview={(
        <PreviewPage
          kicker="Lesson"
          title={form.values.title || ''}
          byline={userAvatar?.name ? `By ${userAvatar.name}` : ''}
          html={form.values.content || ''}
        />
      )}>
        <FormField label="Title" inputField={form.fields.title} className="mb-4">
          <Input
            id="title"
            className="form-control-lg"
            placeholder="What learners will see as the lesson name"
            {...form.fields.title?.getStatusProps()}
            {...form.fields.title?.getProps()}
          />
        </FormField>

        <Card title="Lesson introduction" className="mb-5">
          <HtmlEditor {...form.fields.content?.getProps()} />
        </Card>

        {!isNew && (
          <Button
            onClick={onDelete}
            variant="outline-danger" className="btn-danger-on-hover px-3"
          >
            <Icon icon={icons.delete} className="mr-2" />
            Delete this lesson
          </Button>
        )}
      </EditorWithPreview>
    </>
  )
}


EditLesson.propTypes = {
  lesson: PropTypes.object.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired
}
